import { useState } from "react";
import { Mail, Phone, MapPin, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { submitDemoRequest } from "../lib/api";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";
import Turnstile from "../components/Turnstile";

const empty = { name: "", email: "", organization: "", message: "" };
const captchaEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [token, setToken] = useState("");
  const [widgetKey, setWidgetKey] = useState(0);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaEnabled && !token) { toast.error(t("captcha.required")); return; }
    setLoading(true);
    try {
      await submitDemoRequest({ ...form, source: "contact-page", turnstile_token: token });
      setDone(true); setForm(empty);
      toast.success(t("contact.thankYou"));
    } catch {
      toast.error(t("askjoyn.error")); setToken(""); setWidgetKey((k) => k + 1);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full border border-border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-joyn-orange focus:border-transparent";

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-40 pb-24">
      <Seo {...PAGE_SEO["/contact"]} path="/contact" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-orange mb-4">{t("contact.eyebrow")}</p>
          <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tight leading-[0.98]">{t("contact.titleBefore")}<span className="joyn-gradient-text">{t("contact.titleBrand")}</span></h1>
          <p className="mt-5 text-lg text-muted-foreground max-w-md">{t("contact.desc")}</p>
          <div className="mt-12 space-y-6">
            <a href="mailto:sales@joynams.com" className="group flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-white transition-transform group-hover:-translate-y-0.5" style={{ background: "#F05A28" }}><Mail size={20} /></span>
              <span><span className="block text-xs uppercase tracking-widest text-muted-foreground">{t("contact.email")}</span><span className="font-semibold">sales@joynams.com</span></span>
            </a>
            <a href="tel:+16152498833" className="group flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-white transition-transform group-hover:-translate-y-0.5" style={{ background: "#4BB5E7" }}><Phone size={20} /></span>
              <span><span className="block text-xs uppercase tracking-widest text-muted-foreground">{t("contact.phone")}</span><span className="font-semibold">+1 (615) 249-8833</span></span>
            </a>
            <div className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-white" style={{ background: "#3AAA35" }}><MapPin size={20} /></span>
              <span><span className="block text-xs uppercase tracking-widest text-muted-foreground">{t("contact.location")}</span><span className="font-semibold">{t("contact.locationValue")}</span></span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-3xl overflow-hidden">
          <div className="h-1.5 joyn-gradient-bar" />
          <div className="p-8 md:p-10">
            {done ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <div className="mb-6 h-14 w-14 flex items-center justify-center rounded-2xl text-white" style={{ background: "var(--joyn-green)" }}><Check size={28} /></div>
                <h3 className="font-display font-extrabold text-2xl tracking-tight mb-2">{t("contact.thankYou")}</h3>
                <p className="text-muted-foreground">{t("contact.thankYouMsg")}</p>
                <button data-testid="contact-reset-btn" onClick={() => setDone(false)} className="mt-8 font-semibold text-joyn-orange">{t("contact.another")}</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <label className="block"><span className="block text-sm font-semibold mb-1.5">{t("contact.name")} <span className="text-joyn-orange">*</span></span>
                  <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder={t("contact.namePh")} /></label>
                <label className="block"><span className="block text-sm font-semibold mb-1.5">{t("contact.email")} <span className="text-joyn-orange">*</span></span>
                  <input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder={t("contact.emailPh")} /></label>
                <label className="block"><span className="block text-sm font-semibold mb-1.5">{t("contact.org")} <span className="text-joyn-orange">*</span></span>
                  <input data-testid="contact-input-organization" required value={form.organization} onChange={update("organization")} className={inputCls} placeholder={t("contact.orgPh")} /></label>
                <label className="block"><span className="block text-sm font-semibold mb-1.5">{t("contact.message")}</span>
                  <textarea data-testid="contact-input-message" rows={4} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} placeholder={t("contact.messagePh")} /></label>
                <Turnstile key={widgetKey} onToken={setToken} onExpire={() => setToken("")} />
                <button data-testid="contact-submit-btn" type="submit" disabled={loading} className="group w-full inline-flex items-center justify-center gap-2 bg-foreground text-white font-semibold py-4 rounded-full hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                  {loading ? t("contact.sending") : <>{t("contact.send")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
